import { useState } from 'react';
import { MapPin, Package, Search } from 'lucide-react';

import type { AdminStore } from '@/client/types/stores';
import type { StoreOrder } from '@/client/types/stores/parity';
import { formatMoney } from '@/client/types/payment';
import { useStoreOrderDetailQuery, useStoreOrderListQuery } from '@/client/api/stores/parity.query';
import { useUpdateStoreOrderStatus } from '@/client/api/stores/parity.mutation';
import { PERMISSIONS } from '@/client/config/permissions';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { RelationshipLink, UserRelationshipLink } from '@/client/components/admin/Payment/RelationshipLink';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

import { InfoRow, Pager, Pill, SectionCard, label, shortDate, statusTone, TableSkeleton, CardSkeleton, ListSkeleton, InfoRowsSkeleton } from './shared';
import { ReasonDialog } from './ReasonDialog';
import { ExportButton } from './ExportButton';

const LIMIT = 25;

const STATUS_FILTERS = [
  'pending', 'paid', 'confirmed', 'processing', 'shipped', 'delivered', 'completed',
  'cancelled', 'refund_requested', 'refunded', 'return_requested', 'returned',
];

const OFFLINE_METHOD: Record<string, string> = { pay_on_pickup: 'Pay on pickup', pay_on_delivery: 'Pay on delivery' };
const isOffline = (o: Pick<StoreOrder, 'payment_method'>) => Boolean(o.payment_method && OFFLINE_METHOD[o.payment_method]);
const paymentLabel = (o: Pick<StoreOrder, 'payment_method' | 'date_paid'>) =>
  `${OFFLINE_METHOD[o.payment_method ?? ''] ?? o.payment_method ?? '—'}${isOffline(o) ? (o.date_paid ? ' · Paid' : ' · Awaiting payment') : ''}`;

const orderMoney = (o: Pick<StoreOrder, 'currency_code'>, n: number | null | undefined) =>
  formatMoney(n ?? 0, o.currency_code ?? 'GHS');

/** Next-status actions, as the seller sees them (`allowed_status`). */
const StatusActions: React.FC<{ storeid: number; order: StoreOrder; onDone: () => void }> = ({
  storeid,
  order,
  onDone,
}) => {
  const update = useUpdateStoreOrderStatus();
  const [target, setTarget] = useState<string | null>(null);
  const [tracking, setTracking] = useState(order.tracking_number ?? '');
  const [collected, setCollected] = useState(false);
  const [collectedBy, setCollectedBy] = useState<'seller' | 'rider'>('seller');
  // Offline orders must record the cash collection before they can be delivered.
  const needsCollection = target === 'delivered' && isOffline(order) && !order.date_paid;

  if (!order.allowed_status.length) {
    return <p className="text-xs text-gray-500">No further status changes from “{label(order.status)}”.</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {order.allowed_status.map((s) => (
          <PermissionButton
            key={s}
            code={PERMISSIONS.store.orders}
            size="sm"
            variant={s === 'cancelled' ? 'destructive' : 'outline'}
            onClick={() => setTarget(s)}
          >
            {s === 'cancelled' ? 'Cancel order' : `Mark ${label(s)}`}
          </PermissionButton>
        ))}
      </div>
      {target === 'shipped' && (
        <label className="block space-y-1 text-sm">
          <span className="text-xs font-medium text-gray-500">Tracking number (optional)</span>
          <Input value={tracking} onChange={(e) => setTracking(e.target.value)} className="w-64" />
        </label>
      )}
      {target && (
        <ReasonDialog
          open
          title={target === 'cancelled' ? `Cancel order #${order.orderid}` : `Mark order #${order.orderid} ${label(target)}`}
          description={
            target === 'cancelled'
              ? 'Releases reserved stock. The buyer and seller are notified; the reason is recorded as the cancel reason.'
              : 'Changes the order as if the seller had. The seller is notified with your reason.'
          }
          confirmLabel={target === 'cancelled' ? 'Cancel order' : 'Update'}
          destructive={target === 'cancelled'}
          reasonRequired
          loading={update.isPending}
          confirmDisabled={needsCollection && !collected}
          onConfirm={(reason) =>
            update.mutate(
              {
                storeid,
                orderid: order.orderid,
                status: target,
                reason,
                ...(target === 'shipped' && tracking.trim() ? { tracking_number: tracking.trim() } : {}),
                ...(needsCollection ? { payment_collected: true, collected_by: collectedBy } : {}),
              },
              { onSuccess: () => { setTarget(null); onDone(); } },
            )
          }
          onClose={() => setTarget(null)}
        >
          {needsCollection && (
            <div className="space-y-2 text-sm">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={collected} onChange={(e) => setCollected(e.target.checked)} />
                Payment collected ({orderMoney(order, order.amount_total)})
              </label>
              <label className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-500">Collected by</span>
                <select value={collectedBy} onChange={(e) => setCollectedBy(e.target.value as 'seller' | 'rider')} className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm">
                  <option value="seller">Seller</option>
                  <option value="rider">Rider</option>
                </select>
              </label>
            </div>
          )}
        </ReasonDialog>
      )}
    </div>
  );
};

const addressLine = (a: StoreOrder['shipping_address']): string | null => {
  if (!a) return null;
  return [a.name, a.address, a.city, a.region, a.gpgpsCode, a.phone].filter(Boolean).join(' · ') || null;
};

const StoreOrderDrawer: React.FC<{ storeid: number; orderid: number | null; onClose: () => void }> = ({
  storeid,
  orderid,
  onClose,
}) => {
  const { data: order, isLoading } = useStoreOrderDetailQuery(storeid, orderid);

  return (
    <Sheet open={orderid !== null} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl">
        <SheetHeader className="border-b border-gray-100 p-4 pr-12 text-left">
          <SheetTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-teal-600" /> Order #{orderid}
          </SheetTitle>
          <SheetDescription>{order ? label(order.status) : 'Loading…'}</SheetDescription>
        </SheetHeader>
        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {isLoading || !order ? (
            <>
              <div className="flex items-center justify-between rounded-lg bg-gray-50 p-4">
                <div className="space-y-1.5">
                  <Skeleton className="h-3 w-10" />
                  <Skeleton className="h-7 w-32" />
                </div>
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
              <CardSkeleton><ListSkeleton rows={2} media="thumb" /></CardSkeleton>
              <CardSkeleton><InfoRowsSkeleton rows={4} /></CardSkeleton>
              <CardSkeleton><InfoRowsSkeleton rows={3} /></CardSkeleton>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between rounded-lg bg-gray-50 p-4">
                <div>
                  <p className="text-xs text-gray-500">Total</p>
                  <p className="text-2xl font-black text-gray-900">{orderMoney(order, order.amount_total)}</p>
                </div>
                <Pill tone={statusTone(order.status)}>{label(order.status)}</Pill>
              </div>

              <SectionCard title="Items">
                <div className="divide-y divide-gray-100">
                  {order.order_items.map((it) => (
                    <div key={it.oiid} className="flex items-center gap-3 py-2">
                      <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded bg-gray-100">
                        {it.thumbnail_url && <img src={it.thumbnail_url} alt="" className="h-full w-full object-cover" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-gray-900">{it.name_snapshot ?? `Listing #${it.eid}`}</p>
                        <p className="text-xs text-gray-500">
                          {it.quantity} × {orderMoney(order, it.unit_price)}
                        </p>
                      </div>
                      <span className="text-sm tabular-nums">{orderMoney(order, it.amount)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-2 space-y-0.5 border-t border-gray-100 pt-2">
                  <InfoRow label="Subtotal">{orderMoney(order, order.amount_subtotal)}</InfoRow>
                  <InfoRow label="Shipping">{orderMoney(order, order.amount_shipping)}</InfoRow>
                  <InfoRow label="Tax">{orderMoney(order, order.amount_tax)}</InfoRow>
                </div>
              </SectionCard>

              <SectionCard title="Buyer & delivery">
                <div className="divide-y divide-gray-100">
                  <InfoRow label="Buyer">
                    <UserRelationshipLink uid={order.uid_buyer} summary={order.buyer_username ? { uid: order.uid_buyer ?? 0, username: order.buyer_username } : null} />
                  </InfoRow>
                  <InfoRow label="Delivery">{label(order.delivery_option)}</InfoRow>
                  <InfoRow label="Tracking">{order.tracking_number || '—'}</InfoRow>
                </div>
                {addressLine(order.shipping_address) && (
                  <p className="mt-2 flex items-start gap-1.5 text-sm text-gray-700">
                    <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                    {addressLine(order.shipping_address)}
                  </p>
                )}
                {order.notes && <p className="mt-2 text-sm text-gray-600">Note: {order.notes}</p>}
              </SectionCard>

              <SectionCard title="Payment">
                <div className="divide-y divide-gray-100">
                  <InfoRow label="Method">{isOffline(order) ? paymentLabel(order) : order.payment?.method ?? order.payment_method ?? '—'}</InfoRow>
                  <InfoRow label="Payment">
                    {order.pid != null ? <RelationshipLink kind="payment" id={order.pid} /> : '—'}
                  </InfoRow>
                  <InfoRow label="Invoice">
                    {order.invid != null ? <RelationshipLink kind="invoice" id={order.invid} /> : '—'}
                  </InfoRow>
                  <InfoRow label="Placed">{shortDate(order.date_added)}</InfoRow>
                  <InfoRow label="Paid">{shortDate(order.date_paid)}</InfoRow>
                  <InfoRow label="Shipped">{shortDate(order.date_shipped)}</InfoRow>
                  <InfoRow label="Delivered">{shortDate(order.date_delivered)}</InfoRow>
                </div>
              </SectionCard>

              <SectionCard title="Fulfilment actions" description="The same transitions the seller has in Store Manager.">
                <StatusActions key={order.status} storeid={storeid} order={order} onDone={() => undefined} />
              </SectionCard>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

/**
 * The store's orders exactly as the seller sees them (`GET /<id>/orders`,
 * including orders matched only by line items). Row → drawer with items,
 * delivery, payment and the seller's status actions (`admin.store.orders`).
 */
export const OrdersTab: React.FC<{ store: AdminStore }> = ({ store }) => {
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [openId, setOpenId] = useState<number | null>(null);

  const { data, isLoading, isFetching } = useStoreOrderListQuery(store.storeid, {
    limit: LIMIT,
    offset,
    status: status || undefined,
    search: q.trim() || undefined,
  });

  const orders = data?.orders ?? [];
  const total = data?.total_count ?? 0;

  return (
    <SectionCard
      bare
      description="Every order placed with this store."
      actions={
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{isFetching ? 'Refreshing…' : `${total} total`}</span>
          <ExportButton storeid={store.storeid} resource="orders" params={{ status: status || undefined }} />
        </div>
      }
    >
      <div className="mb-3 flex flex-wrap gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            value={q}
            onChange={(e) => { setQ(e.target.value); setOffset(0); }}
            placeholder="Search order reference"
            className="pl-8"
          />
        </div>
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); setOffset(0); }}
          className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          {STATUS_FILTERS.map((s) => (
            <option key={s} value={s}>{label(s)}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <TableSkeleton cols={6} />
      ) : orders.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-400">No orders match.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-gray-400">
                <th className="py-2 pr-4">Order</th>
                <th className="py-2 pr-4">Buyer</th>
                <th className="py-2 pr-4">Placed</th>
                <th className="py-2 pr-4">Items</th>
                <th className="py-2 pr-4">Total</th>
                <th className="py-2 pr-4">Tracking</th>
                <th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {orders.map((o) => (
                <tr key={o.orderid} onClick={() => setOpenId(o.orderid)} className="cursor-pointer hover:bg-gray-50">
                  <td className="py-2 pr-4 font-medium text-gray-900">#{o.orderid}</td>
                  <td className="py-2 pr-4 text-gray-700">{o.buyer_username ? `@${o.buyer_username}` : o.uid_buyer ? `User #${o.uid_buyer}` : '—'}</td>
                  <td className="py-2 pr-4 text-gray-600">{shortDate(o.date_added)}</td>
                  <td className="py-2 pr-4 tabular-nums text-gray-600">{o.item_count ?? '—'}</td>
                  <td className="py-2 pr-4 tabular-nums font-medium">{orderMoney(o, o.amount_total)}</td>
                  <td className="py-2 pr-4 font-mono text-xs text-gray-500">{o.tracking_number || '—'}</td>
                  <td className="py-2">
                    <Pill tone={statusTone(o.status)}>{label(o.status)}</Pill>
                    {isOffline(o) && <p className="mt-0.5 text-xs text-gray-500">{paymentLabel(o)}</p>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pager total={total} limit={LIMIT} offset={offset} onChange={setOffset} />
      <StoreOrderDrawer storeid={store.storeid} orderid={openId} onClose={() => setOpenId(null)} />
    </SectionCard>
  );
};
